import { openConversionModal, getFunnelPageContext } from "./utils/conversion-modal";
import { MultipleChoiceQuestion } from "./multiple-choice-question";
import { PLAN_YEARLY_PER_MONTH } from "../common/pricing";

function setupQuestionExtraContent(question: MultipleChoiceQuestion) {
  const extraContent = question.querySelector(
    ".enem-exercises-results-list-item-extra-content"
  );
  if (!extraContent) return;

  const questionExplanationTab = extraContent.querySelector<HTMLInputElement>(
    'input[data-tab="explanation"]'
  );
  const questionExplanationContent = extraContent.querySelector<HTMLElement>(
    ".enem-exercises-results-list-item-extra-content-body--explanation"
  );
  const questionRelatedContentTab =
    extraContent.querySelector<HTMLInputElement>(
      'input[data-tab="related-content"]'
    );

  function onChangeExtraContentTab(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    const target = e.target as HTMLInputElement;
    if (!question.submitted) {
      target.checked = false;
    }
  }

  function onClickExtraContentTab(e: Event) {
    const target = e.target as HTMLInputElement;
    if (!question.submitted) {
      e.preventDefault();
      e.stopPropagation();
      target.checked = false;
      return;
    }

    switch (target.dataset.tab) {
      case "explanation":
        showExplanation(false);
        break;
      case "related-content":
        showRelatedContent();
        break;
      default:
        break;
    }
  }

  function uncheckTab(tabElement: HTMLInputElement | null) {
    if (tabElement) {
      tabElement.checked = false;
    }
  }

  function showExplanation(shouldScroll: boolean = true) {
    if (
      !questionExplanationContent ||
      !questionExplanationTab ||
      !question.submitted
    ) {
      uncheckTab(questionExplanationTab);
      return;
    }

    function lockExplanation(container: HTMLElement) {
      if (!question.explanationLimitReached) {
        container.innerHTML = question.explanation || "";
        return;
      }

      const isCorrect = question.isCorrect;
      const explanation = question.explanation || "";
      const correctLetter = question.correctOptionLetter || "A";
      const context = isCorrect ? "gabarito-right" as const : "gabarito-wrong" as const;

      container.innerHTML = "";
      container.classList.add("locked");

      // O paywall_question_limit saiu: o funnel_cta_view do botão abaixo (via
      // funnel-tracker) já conta a mesma impressão, com correct/incorrect no
      // element — e deixar de o enviar liberta a custom dimension page_section.

      const cta = document.createElement("div");
      cta.className = `gabarito-cta gabarito-cta--${isCorrect ? "right" : "wrong"}`;

      if (isCorrect) {
        cta.innerHTML = `
          <div class="gabarito-cta__streak">\u2713 Acertou!</div>
          <div class="gabarito-cta__headline">Voc\u00ea acertou \u2014 mas sabe<br>explicar por qu\u00ea?</div>
          <div class="gabarito-cta__sub">O gabarito comentado confirma o seu racioc\u00ednio e mostra o que diferencia a ${correctLetter} das outras alternativas \u2014 essencial para ir bem na prova.</div>
        `;
      } else {
        cta.innerHTML = `
          <div class="gabarito-cta__icon">\uD83D\uDD25</div>
          <div class="gabarito-cta__headline">Seu esfor\u00e7o merece explica\u00e7\u00e3o,<br>n\u00e3o s\u00f3 a resposta.</div>
          <div class="gabarito-cta__sub">Veja o racioc\u00ednio completo por tr\u00e1s da alternativa ${correctLetter} \u2014 para entender de verdade e n\u00e3o errar de novo.</div>
        `;
      }

      // Blurred preview
      if (explanation) {
        const blurPreview = document.createElement("div");
        blurPreview.className = "gabarito-cta__blur-preview";
        blurPreview.innerHTML = `
          <div class="gabarito-cta__blur-text">${explanation}</div>
          <div class="gabarito-cta__blur-overlay${isCorrect ? " gabarito-cta__blur-overlay--green" : ""}"></div>
        `;
        cta.appendChild(blurPreview);
      }

      // Button
      const button = document.createElement("button");
      button.className = `gabarito-cta__button gabarito-cta__button--${isCorrect ? "right" : "wrong"}`;
      button.textContent = isCorrect ? "Ver explica\u00e7\u00e3o completa \u2192" : "Ver gabarito comentado completo \u2192";
      // funnel_cta_view / funnel_cta_click come from events/funnel-tracker, which
      // picks the button up via its MutationObserver \u2014 so the attributes must be
      // set before the CTA is appended to the DOM (further down).
      const { location: funnelLocation, elementPrefix } = getFunnelPageContext();
      button.setAttribute("data-funnel-click-location", funnelLocation);
      button.setAttribute(
        "data-funnel-click-element",
        `gabarito_cta_${elementPrefix}_${isCorrect ? "correct" : "incorrect"}`
      );
      button.setAttribute("data-funnel-click-type", "limit");
      button.addEventListener("click", () => openConversionModal(context));
      cta.appendChild(button);

      // Trust line
      const trust = document.createElement("div");
      trust.className = "gabarito-cta__trust";
      trust.textContent = `Assine o TM+ \u00b7 a partir de ${PLAN_YEARLY_PER_MONTH}/m\u00eas \u00b7 cancele quando quiser`;
      cta.appendChild(trust);

      container.appendChild(cta);
    }

    lockExplanation(questionExplanationContent);
    questionExplanationTab.checked = true;
    if (shouldScroll) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (!extraContent) return;
          const extraContentContainerRect = extraContent.getBoundingClientRect();
          const extraContentVisibleHeight = 400;
          window.scrollTo({
            top: extraContentContainerRect.top + window.scrollY - (window.innerHeight - extraContentVisibleHeight),
            behavior: "smooth",
          });
        }, 100);
      });
    }
  }

  function showRelatedContent() {
    if (
      !questionRelatedContentTab ||
      !question.submitted
    ) {
      uncheckTab(questionRelatedContentTab);
      return;
    }

    questionRelatedContentTab.checked = true;
  }

  questionExplanationTab?.addEventListener("change", onChangeExtraContentTab);
  questionExplanationTab?.addEventListener("click", onClickExtraContentTab);
  questionRelatedContentTab?.addEventListener("change", onChangeExtraContentTab);
  questionRelatedContentTab?.addEventListener("click", onClickExtraContentTab);

  question.addEventListener("answer-submitted", () => {
    showExplanation();
  });
}

function setup() {
  const questions = document.querySelectorAll<MultipleChoiceQuestion>(
    "multiple-choice-question"
  );
  questions.forEach(setupQuestionExtraContent);
}

window.addEventListener("DOMContentLoaded", setup);
